import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Grid,
  Divider,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
  useTheme
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Description as DocumentIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import ApiService from '../../services/api.service';
import { formatDate } from '../../utils/dateFormat';
import { getBrandedTableHeadSx } from '../../utils/colorUtils';

const STATUS_CONFIG = {
  PENDING_REVIEW: { color: 'warning', label: 'Pending Review' },
  OPEN: { color: 'info', label: 'Open' },
  IN_PROGRESS: { color: 'warning', label: 'In Progress' },
  COMPLETED: { color: 'success', label: 'Completed' },
  PARTIALLY_INVOICED: { color: 'info', label: 'Partly invoiced' },
  INVOICED: { color: 'primary', label: 'Invoiced' },
  CANCELLED: { color: 'error', label: 'Cancelled' }
};

const toLabel = (s) => (s || '').toLowerCase().split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

const StatusChip = ({ status }) => {
  const config = STATUS_CONFIG[status] || { color: 'default', label: toLabel(status) };
  return <Chip label={config.label} color={config.color} size="small" />;
};

const FS = '0.8125rem';
const SECTION_TITLE_SX = { fontSize: '0.875rem', fontWeight: 600, mb: 1.5 };
const LABEL_SX = { fontSize: '0.75rem', color: 'text.secondary' };
const VALUE_SX = { fontSize: FS, fontWeight: 500 };
const BTN_SX = { textTransform: 'none', fontSize: FS };

const Section = ({ title, children, sx }) => (
  <Paper sx={{ p: 2, ...sx }}>
    {title && <Typography sx={SECTION_TITLE_SX}>{title}</Typography>}
    {children}
  </Paper>
);

const Field = ({ label, children, sx }) => (
  <Box sx={sx}>
    <Typography sx={LABEL_SX}>{label}</Typography>
    <Box sx={VALUE_SX}>{children}</Box>
  </Box>
);

const TotalRow = ({ label, value, strong }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 4, py: 0.25 }}>
    <Typography sx={{ fontSize: FS, fontWeight: strong ? 600 : 400, color: strong ? 'text.primary' : 'text.secondary' }}>{label}</Typography>
    <Typography sx={{ fontSize: strong ? '0.875rem' : FS, fontWeight: strong ? 700 : 500, color: strong ? 'primary.main' : 'text.primary' }}>{value}</Typography>
  </Box>
);

const formatCurrency = (value) => `AED ${parseFloat(value || 0).toFixed(2)}`;

const PortalWorkOrderDetail = ({ portalUser }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { workOrderId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [workOrder, setWorkOrder] = useState(null);

  // A B2B's portal offers only internal services (with resolved prices), so show prices even while
  // PENDING_REVIEW; a normal TC hides them until it reviews/prices the WO.
  let isB2b = false;
  try { isB2b = JSON.parse(localStorage.getItem('portalOrganization') || '{}')?.isB2b === true; } catch (e) { isB2b = false; }

  useEffect(() => {
    fetchWorkOrder();
  }, [workOrderId]);

  const fetchWorkOrder = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await ApiService.getPortalWorkOrder(workOrderId);
      setWorkOrder(response.data?.data || null);
    } catch (err) {
      console.error('Failed to fetch work order:', err);
      setError('Failed to load work order details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        sx={{ mb: 2 }}
        action={<Button size="small" onClick={fetchWorkOrder} sx={BTN_SX}>Retry</Button>}
      >
        {error}
      </Alert>
    );
  }

  if (!workOrder) {
    return (
      <Alert severity="warning">
        Work Order not found
      </Alert>
    );
  }

  const lineItems = workOrder.line_items || workOrder.lineItems || [];
  const showPrices = workOrder.status !== 'PENDING_REVIEW' || isB2b;
  const progress = workOrder.progress_percent || 0;

  return (
    <Box sx={{ width: 0, minWidth: '100%' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Tooltip title="Back to Work Orders">
          <IconButton size="small" onClick={() => navigate('/portal/work-orders')}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Typography sx={{ fontSize: '1rem', fontWeight: 600 }}>{workOrder.work_order_number}</Typography>
        <StatusChip status={workOrder.status} />
        {workOrder.title && (
          <Typography noWrap sx={{ fontSize: FS, color: 'text.secondary', minWidth: 0, flex: '1 1 200px' }}>
            {workOrder.title}
          </Typography>
        )}
      </Box>

      {/* Details + Totals */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={8}>
          <Section title="Work Order Details" sx={{ height: '100%' }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' }, gap: 2 }}>
              <Field label="Title" sx={{ gridColumn: '1 / -1', wordBreak: 'break-word' }}>{workOrder.title || '—'}</Field>
              <Field label="Work Order Number">{workOrder.work_order_number || '—'}</Field>
              <Field label="Order Date">{formatDate(workOrder.order_date, '—')}</Field>
              {workOrder.quotation_number && <Field label="From Quotation">{workOrder.quotation_number}</Field>}
              {workOrder.invoice_number && <Field label="Invoice">{workOrder.invoice_number}</Field>}
            </Box>
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography sx={LABEL_SX}>Progress</Typography>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                  {workOrder.completed_tasks || 0} of {workOrder.total_tasks || 0} tasks · {progress}%
                </Typography>
              </Box>
              <LinearProgress variant="determinate" value={progress} sx={{ height: 6, borderRadius: 3 }} />
            </Box>
          </Section>
        </Grid>
        <Grid item xs={12} md={4}>
          <Section title="Totals" sx={{ height: '100%' }}>
            {!showPrices ? (
              <Alert severity="info" sx={{ fontSize: FS }}>
                <Typography sx={{ fontSize: FS, fontWeight: 500 }}>Amount will be confirmed after review</Typography>
                <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                  Your typing center will review the requested services and publish
                  the final pricing here once the work order is processed.
                </Typography>
              </Alert>
            ) : (
              <>
                <TotalRow label="Items" value={lineItems.length} />
                <TotalRow label="Subtotal" value={formatCurrency(workOrder.subtotal)} />
                <TotalRow label="VAT" value={formatCurrency(workOrder.tax_total)} />
                <Divider sx={{ my: 0.75 }} />
                <TotalRow label="Grand Total" value={formatCurrency(workOrder.grand_total)} strong />
              </>
            )}
          </Section>
        </Grid>
      </Grid>

      {/* Line Items */}
      <Paper sx={{ mb: 2 }}>
        <Typography sx={{ ...SECTION_TITLE_SX, px: 2, pt: 1.5, mb: 1 }}>Line Items</Typography>
        {!showPrices && (
          <Alert severity="info" sx={{ mx: 2, mb: 1.5, fontSize: FS }}>
            Pricing will appear here after the typing center reviews this work order.
          </Alert>
        )}
        <TableContainer>
          <Table size="small">
            <TableHead sx={getBrandedTableHeadSx(theme)}>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Service</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell align="right">Qty</TableCell>
                {showPrices && (
                  <>
                    <TableCell align="right">Govt Charge</TableCell>
                    <TableCell align="right">Service Charge</TableCell>
                    <TableCell align="right">VAT</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {lineItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={showPrices ? 8 : 4} align="center" sx={{ py: 3, color: 'text.secondary' }}>No line items</TableCell>
                </TableRow>
              ) : (
                lineItems.map((item, index) => {
                  const serviceChargeDisplay =
                    parseFloat(item.service_charge || 0) + parseFloat(item.markup_amount || 0);
                  const taxDisplay =
                    parseFloat(item.tax_value || 0) + parseFloat(item.tax_on_markup || 0);
                  return (
                    <TableRow key={item.id || index}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>{item.service_name}</TableCell>
                      <TableCell>{item.customer_name || '—'}</TableCell>
                      <TableCell align="right">{item.quantity || 1}</TableCell>
                      {showPrices && (
                        <>
                          <TableCell align="right">{formatCurrency(item.govt_charge)}</TableCell>
                          <TableCell align="right">{formatCurrency(serviceChargeDisplay)}</TableCell>
                          <TableCell align="right">{formatCurrency(taxDisplay)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>{formatCurrency(item.line_total)}</TableCell>
                        </>
                      )}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Documents */}
      {workOrder.documents && workOrder.documents.length > 0 && (
        <Section title="Documents" sx={{ mb: 2 }}>
          <List dense disablePadding>
            {workOrder.documents.map((doc, index) => (
              <ListItem key={doc.id || index} disableGutters>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <DocumentIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                </ListItemIcon>
                <ListItemText
                  primary={doc.file_name}
                  secondary={doc.description || doc.document_type}
                  primaryTypographyProps={{ fontSize: FS }}
                  secondaryTypographyProps={{ fontSize: '0.75rem' }}
                />
              </ListItem>
            ))}
          </List>
        </Section>
      )}

      {/* Notes */}
      {workOrder.notes && (
        <Section title="Notes">
          <Typography sx={{ fontSize: FS, whiteSpace: 'pre-line' }}>{workOrder.notes}</Typography>
        </Section>
      )}
    </Box>
  );
};

export default PortalWorkOrderDetail;
